'use client';

import { useEffect, useMemo, useState } from 'react';

import type { EmissionFactorSearchContext } from '../hooks/useEmissionFactorSearchState';
import type { EmissionFactor, EmissionFactorSearchParams } from '../types';

import { EmissionFactorSearch } from './EmissionFactorSearch';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTranslation } from 'react-i18next';

interface ActivityAssignmentFactorOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activityDescription: string;
  searchContext: EmissionFactorSearchContext;
  searchInitialParams?: Partial<EmissionFactorSearchParams>;
  initialMode?: 'manual' | 'search';
  initialManualFactorKgCo2ePerUnit?: string;
  onApplyManualFactor: (manualFactorKgCo2ePerUnit: string) => void;
  onSelectFactor: (factorId: string, factor?: EmissionFactor) => void;
}

export function ActivityAssignmentFactorOverrideDialog({
  open,
  onOpenChange,
  activityDescription,
  searchContext,
  searchInitialParams,
  initialMode = 'search',
  initialManualFactorKgCo2ePerUnit = '',
  onApplyManualFactor,
  onSelectFactor,
}: ActivityAssignmentFactorOverrideDialogProps) {
  const { t } = useTranslation('ghg');
  const [mode, setMode] = useState<'manual' | 'search'>(initialMode);
  const [manualFactorKgCo2ePerUnit, setManualFactorKgCo2ePerUnit] = useState(
    initialManualFactorKgCo2ePerUnit
  );

  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setManualFactorKgCo2ePerUnit(initialManualFactorKgCo2ePerUnit);
  }, [initialManualFactorKgCo2ePerUnit, initialMode, open]);

  const parsedManualFactor = useMemo(() => {
    if (!manualFactorKgCo2ePerUnit.trim()) return null;
    const value = Number(manualFactorKgCo2ePerUnit);
    return Number.isFinite(value) && value >= 0 ? value : null;
  }, [manualFactorKgCo2ePerUnit]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('activityAssignmentFactorOverrideDialog.dialogTitle_override_emission_factor')}</DialogTitle>
          <DialogDescription>{t('activityAssignmentFactorOverrideDialog.dialogDesc_provide_a_direct_manual_factor_or_search')}</DialogDescription>
        </DialogHeader>
        <Tabs value={mode} onValueChange={(value) => { setMode(value as 'manual' | 'search'); }}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manual">Manual factor</TabsTrigger>
            <TabsTrigger value="search">Search factors</TabsTrigger>
          </TabsList>

          <TabsContent value="manual" className="space-y-4">
            <div className="space-y-4 rounded-md border bg-muted/10 p-4">
              <div className="space-y-1">
                <p className="text-sm font-medium">Direct kg CO2e/unit</p>
                <p className="text-xs text-muted-foreground">
                  Use this when you already know the exact factor and want to bypass factor search.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="assignment-manual-factor-input">{t('activityAssignmentFactorOverrideDialog.label_direct_kg_co2e_unit')}</Label>
                <Input
                  id="assignment-manual-factor-input"
                  type="number"
                  min="0"
                  step="any"
                  value={manualFactorKgCo2ePerUnit}
                  onChange={(event) => {
                    setManualFactorKgCo2ePerUnit(event.target.value);
                  }}
                  placeholder={t('placeholder.eg42')}
                />
                {manualFactorKgCo2ePerUnit.trim() && parsedManualFactor == null ? (
                  <p className="text-xs text-destructive">
                    Enter a valid non-negative manual factor.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    The entered value will be used as the final emission factor for this row.
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    onOpenChange(false);
                  }}
                >
                  {t('activityAssignmentFactorOverrideDialog.btn_cancel')}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    if (parsedManualFactor == null) return;
                    onApplyManualFactor(manualFactorKgCo2ePerUnit);
                    onOpenChange(false);
                  }}
                  disabled={parsedManualFactor == null}
                >
                  Use Manual Factor
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="search">
            <EmissionFactorSearch
              {...(searchInitialParams ? { initialParams: searchInitialParams } : {})}
              context={searchContext}
              onSelectFactor={(factorId, factor) => {
                onSelectFactor(factorId, factor);
                onOpenChange(false);
              }}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
